export const DEFAULT_LOT_QTY = 0.05
export const DEFAULT_NEAR_PCT = 1
export const DEFAULT_FAR_PCT = 3
export const DEFAULT_TARGET_MARGIN_PCT = 0.6

export function resolveAction(hoverPrice, currentPrice) {
  const hover = Number(hoverPrice)
  const current = Number(currentPrice)
  if (!(hover > 0) || !(current > 0) || hover === current) return null
  return hover < current ? 'buy' : 'sell'
}

export function computeWeightedNetPnl({
  longsQty = 0,
  longsNotional = 0,
  shortsQty = 0,
  shortsNotional = 0,
  markPrice = 0,
} = {}) {
  const longsAvg = longsQty > 0 ? longsNotional / longsQty : 0
  const shortsAvg = shortsQty > 0 ? shortsNotional / shortsQty : 0
  const totalLongPnL = longsQty * (markPrice - longsAvg)
  const totalShortPnL = shortsQty * (shortsAvg - markPrice)
  return {
    longsAvg,
    shortsAvg,
    totalLongPnL,
    totalShortPnL,
    totalNetPnl: totalLongPnL + totalShortPnL,
  }
}

export function applyTrade({
  action,
  price,
  qty,
  longsQty = 0,
  longsNotional = 0,
  shortsQty = 0,
  shortsNotional = 0,
} = {}) {
  const tradeQty = Number(qty) || 0
  const tradePrice = Number(price) || 0

  if (action === 'buy') {
    return {
      longsQty: longsQty + tradeQty,
      longsNotional: longsNotional + tradeQty * tradePrice,
      shortsQty,
      shortsNotional,
    }
  }

  return {
    longsQty,
    longsNotional,
    shortsQty: shortsQty + tradeQty,
    shortsNotional: shortsNotional + tradeQty * tradePrice,
  }
}

export function markNetPnl(book, markPrice) {
  return computeWeightedNetPnl({
    ...book,
    markPrice,
  }).totalNetPnl
}

export function resolveBandPcts(nearPct = DEFAULT_NEAR_PCT, farPct = DEFAULT_FAR_PCT) {
  const near = Math.max(Number(nearPct) || DEFAULT_NEAR_PCT, 0.1)
  const far = Math.max(Number(farPct) || DEFAULT_FAR_PCT, 0.1)
  return {
    nearPct: Math.min(near, far),
    farPct: Math.max(near, far),
  }
}

export function resolveTargetMarginPct(pct = DEFAULT_TARGET_MARGIN_PCT) {
  const n = Number(pct)
  return n > 0 ? n : DEFAULT_TARGET_MARGIN_PCT
}

export function isTargetInMoveWindow(target, fromPrice, toPrice) {
  if (toPrice > fromPrice) return target > fromPrice && target <= toPrice
  return target < fromPrice && target >= toPrice
}

export function inventoryCloseInMargin({
  lots = [],
  side,
  fromPrice,
  toPrice,
  targetPctFrac,
} = {}) {
  let qty = 0
  let pnl = 0

  for (const lot of lots) {
    const price = Number(lot?.price || 0)
    const lotQty = Number(lot?.qty || 0)
    if (price <= 0 || lotQty <= 0) continue

    const target = side === 'long'
      ? price * (1 + targetPctFrac)
      : price * (1 - targetPctFrac)
    if (!isTargetInMoveWindow(target, fromPrice, toPrice)) continue

    qty += lotQty
    pnl += lotQty * price * targetPctFrac
  }

  return { qty, pnl }
}

export function computeHoverSim({
  hoverPrice,
  currentPrice,
  qty = DEFAULT_LOT_QTY,
  nearPct = DEFAULT_NEAR_PCT,
  farPct = DEFAULT_FAR_PCT,
  targetMarginPct = DEFAULT_TARGET_MARGIN_PCT,
  longsQty = 0,
  longsNotional = 0,
  shortsQty = 0,
  shortsNotional = 0,
  longLots = [],
  shortLots = [],
} = {}) {
  const action = resolveAction(hoverPrice, currentPrice)
  const tradeQty = Number(qty) || 0
  if (!action || tradeQty <= 0) return null

  const pcts = resolveBandPcts(nearPct, farPct)
  const sign = action === 'buy' ? -1 : 1
  const baselineBook = { longsQty, longsNotional, shortsQty, shortsNotional }
  const book = applyTrade({
    action,
    price: hoverPrice,
    qty: tradeQty,
    ...baselineBook,
  })

  const bands = [
    { key: 'near', pct: pcts.nearPct },
    { key: 'far', pct: pcts.farPct },
  ].map((band) => {
    const markPrice = hoverPrice * (1 + sign * band.pct / 100)
    const marked = computeWeightedNetPnl({ ...book, markPrice })
    const baseline = computeWeightedNetPnl({ ...baselineBook, markPrice })
    return {
      ...band,
      markPrice,
      longPnl: marked.totalLongPnL,
      shortPnl: marked.totalShortPnL,
      netPnl: marked.totalNetPnl,
      baselinePnl: baseline.totalNetPnl,
      deltaPnl: marked.totalNetPnl - baseline.totalNetPnl,
    }
  })

  const targetPct = resolveTargetMarginPct(targetMarginPct)
  const targetFrac = targetPct / 100
  const targetSign = action === 'buy' ? 1 : -1
  const targetMarkPrice = hoverPrice * (1 + targetSign * targetFrac)
  const inventorySide = action === 'buy' ? 'long' : 'short'
  const inventory = inventoryCloseInMargin({
    lots: inventorySide === 'long' ? longLots : shortLots,
    side: inventorySide,
    fromPrice: hoverPrice,
    toPrice: targetMarkPrice,
    targetPctFrac: targetFrac,
  })
  const newPositionPnl = tradeQty * hoverPrice * targetFrac

  return {
    action,
    entryPrice: hoverPrice,
    qty: tradeQty,
    ...pcts,
    targetMarginPct: targetPct,
    bands,
    targetBand: {
      key: 'target',
      pct: targetPct,
      markPrice: targetMarkPrice,
      direction: targetSign > 0 ? 'up' : 'down',
      newPositionPnl,
      inventoryPnl: inventory.pnl,
      inventoryQty: inventory.qty,
      netPnl: newPositionPnl + inventory.pnl,
    },
  }
}
